import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, FileText, FlaskConical, Radar, FileBarChart2,
  AlertTriangle, Upload, ScanLine, ArrowRight, ShieldCheck, Gift, Copy, Twitter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LEVEL = {
  low:      { color: "text-emerald-600", bg: "bg-emerald-50", label: "Healthy" },
  medium:   { color: "text-amber-600",   bg: "bg-amber-50",   label: "Watch" },
  high:     { color: "text-orange-600",  bg: "bg-orange-50",  label: "At risk" },
  critical: { color: "text-rose-600",    bg: "bg-rose-50",    label: "Critical" },
};
const SEV = {
  low:      "text-emerald-700 bg-emerald-50 border-emerald-200",
  medium:   "text-amber-700 bg-amber-50 border-amber-200",
  high:     "text-orange-700 bg-orange-50 border-orange-200",
  critical: "text-rose-700 bg-rose-50 border-rose-200",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [riskier, setRiskier] = useState([]);  // version groups whose latest scan got worse
  const [referrals, setReferrals] = useState(null);  // {code, share_url, bonus_scans, ...}
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    // Riskier-contracts tile: pull workspace-wide version scores and find
    // every group where the most recent scored version is meaningfully
    // worse (≥5 point drop) than the one before it. Fail-closed: any
    // error or empty workspace simply hides the tile.
    api.get("/documents/version-scores").then((r) => {
      const groups = r.data?.groups || {};
      const hits = [];
      for (const [gid, pts] of Object.entries(groups)) {
        const scored = (pts || [])
          .filter((p) => p.score != null)
          .sort((a, b) => a.version_number - b.version_number);
        if (scored.length < 2) continue;
        const latest = scored[scored.length - 1];
        const prev = scored[scored.length - 2];
        const delta = latest.score - prev.score;
        if (delta <= -5) {
          hits.push({
            group_id: gid,
            doc_id: latest.doc_id,
            doc_name: latest.doc_name || "Untitled",
            latest_version: latest.version_number,
            previous_version: prev.version_number,
            latest_score: latest.score,
            previous_score: prev.score,
            level: latest.level,
            delta,
          });
        }
      }
      // Sort worst first.
      hits.sort((a, b) => a.delta - b.delta);
      setRiskier(hits);
    }).catch(() => {});
    // Referral card — kept tiny: a single fetch on mount, no polling.
    // On every fetch, compare `credited` to the last-seen value persisted
    // in localStorage. If it went up, fire a celebratory toast — turns
    // the asynchronous referral payoff into an immediate dopamine hit
    // and meaningfully bumps re-shares. Falls back silently if storage
    // is unavailable.
    const REF_SEEN_KEY = "agentflow_ref_seen_credited";
    const fetchReferrals = () => {
      api.get("/me/referrals").then((r) => {
        const next = r.data;
        setReferrals(next);
        try {
          const seenRaw = localStorage.getItem(REF_SEEN_KEY);
          const seen = seenRaw == null ? null : parseInt(seenRaw, 10);
          const credited = next?.credited ?? 0;
          // First visit: just stash the baseline so we don't toast for
          // pre-existing credits.
          if (seen == null || Number.isNaN(seen)) {
            localStorage.setItem(REF_SEEN_KEY, String(credited));
            return;
          }
          if (credited > seen) {
            const delta = credited - seen;
            const bonus = (next?.referral_bonus_per_event || 3) * delta;
            toast.success(
              delta === 1
                ? `🎁 You earned +${bonus} bonus scans!`
                : `🎁 ${delta} friends signed up — +${bonus} bonus scans earned!`,
              {
                description: delta === 1
                  ? "A friend you invited just ran their first scan."
                  : "Your invited friends just ran their first scans.",
                duration: 8000,
              },
            );
            localStorage.setItem(REF_SEEN_KEY, String(credited));
          }
        } catch { /* localStorage disabled — silently skip */ }
      }).catch(() => {});
    };
    fetchReferrals();
    // Refresh when the tab regains focus — the referee's first scan
    // happens out-of-band, so users who come back to the Dashboard tab
    // get the toast as soon as the credit fires upstream.
    const onVis = () => { if (document.visibilityState === "visible") fetchReferrals(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const level = data ? (LEVEL[data.level] || LEVEL.medium) : LEVEL.low;
  const trendUp = (data?.trend || 0) > 0;
  const trendDown = (data?.trend || 0) < 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Compliance Overview</p>
          <h1 className="mt-1.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Welcome back.
          </h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Here's where your workspace stands today.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate("/documents")} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="quick-upload">
            <Upload className="h-4 w-4 mr-2" /> Upload document
          </Button>
          <Button onClick={() => navigate("/scans")} variant="outline" className="rounded-lg border-slate-300" data-testid="quick-scan">
            <ScanLine className="h-4 w-4 mr-2" /> Run scan
          </Button>
          <Button onClick={() => navigate("/reports")} variant="outline" className="rounded-lg border-slate-300" data-testid="quick-report">
            <FileBarChart2 className="h-4 w-4 mr-2" /> Generate report
          </Button>
        </div>
      </div>

      {/* Score + metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="risk-score-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-blue-600" /> Compliance risk score
          </div>
          <div className="mt-3 flex items-end gap-3">
            <div className="font-display text-6xl font-semibold tracking-tight text-slate-900 tabular-nums">
              {data?.score ?? "—"}
            </div>
            <div className={"mb-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold " + level.bg + " " + level.color}>
              {level.label}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm">
            {trendUp && <span className="inline-flex items-center gap-1 text-rose-600"><TrendingUp className="h-4 w-4" />+{data.trend}</span>}
            {trendDown && <span className="inline-flex items-center gap-1 text-emerald-600"><TrendingDown className="h-4 w-4" />{data.trend}</span>}
            {!trendUp && !trendDown && <span className="text-slate-500">No change vs 7 days ago</span>}
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">Higher = healthier</span>
          </div>
          <div className="mt-5 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={"h-full rounded-full " + (
                data?.level === "critical" ? "bg-rose-500" :
                data?.level === "high" ? "bg-orange-500" :
                data?.level === "medium" ? "bg-amber-500" :
                "bg-emerald-500")}
              style={{ width: `${data?.score || 0}%` }}
            />
          </div>
        </div>

        <Metric icon={AlertTriangle} label="Open issues" value={data?.open_issues} sub={`${data?.high_open_issues || 0} high / critical`} testId="metric-open-issues" />
        <Metric icon={FileText} label="Documents" value={data?.documents} sub="In this workspace" testId="metric-documents" />
        <Metric icon={FlaskConical} label="Scans run" value={data?.scans} sub="Lifetime" testId="metric-scans" />
        <Metric icon={Radar} label="Active monitors" value={data?.monitoring_enabled} sub="Daily / weekly jobs" testId="metric-monitors" />
        <Metric icon={FileBarChart2} label="Reports" value={data?.reports} sub="Generated" testId="metric-reports" />
      </div>

      {/* Refer a friend — small, friendly. Shows the user's share link,
          current bonus_scans balance, and a one-click X share with the
          ref code already attached. Always visible; collapses nothing. */}
      {referrals && (
        <div
          className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-cyan-50/50 p-5 shadow-sm"
          data-testid="referral-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="shrink-0 rounded-xl bg-blue-600 text-white h-10 w-10 inline-flex items-center justify-center">
              <Gift className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-base font-semibold text-slate-900">
                  Refer a friend, get +{referrals.referral_bonus_per_event} scans
                </h3>
                {referrals.bonus_scans > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 px-2 py-0.5 text-[11px] font-semibold tabular-nums" data-testid="referral-bonus-balance">
                    +{referrals.bonus_scans} bonus scan{referrals.bonus_scans === 1 ? "" : "s"} available
                  </span>
                )}
                {referrals.credited > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 ring-1 ring-blue-200 px-2 py-0.5 text-[11px] font-semibold tabular-nums">
                    {referrals.credited} friend{referrals.credited === 1 ? "" : "s"} joined
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Both you and your friend get +{referrals.referral_bonus_per_event} bonus scans the moment they run their first contract scan.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  readOnly
                  value={referrals.share_url}
                  className="flex-1 min-w-[200px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="referral-share-url"
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(referrals.share_url);
                      toast.success("Link copied", { description: "Share it with anyone who'd benefit from AgentFlow." });
                    } catch {
                      toast.error("Couldn't copy", { description: "Select the link manually instead." });
                    }
                  }}
                  className="h-9"
                  data-testid="referral-copy-btn"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const text = `I just discovered @AgentflowTool — AI compliance for SMBs that flags risky contract clauses in seconds. Get +3 free scans when you sign up via my link:`;
                    const shareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referrals.share_url)}`;
                    window.open(shareUrl, "_blank", "noopener,noreferrer,width=560,height=620");
                  }}
                  className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="referral-share-x-btn"
                >
                  <Twitter className="h-3.5 w-3.5 mr-1.5" /> Share on X
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Riskier-contracts tile — surfaces version groups where the latest
          scan score dropped vs the previous version. Only renders when
          there's at least one match so the dashboard stays clean for
          first-time users. */}
      {riskier.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-6 shadow-sm" data-testid="riskier-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-rose-700">
                <AlertTriangle className="h-3.5 w-3.5" /> Contracts that got riskier
              </div>
              <p className="mt-1 text-sm text-slate-700">
                A counterparty revision lowered the safety score on{" "}
                <span className="font-semibold text-slate-900">{riskier.length}</span>{" "}
                contract{riskier.length === 1 ? "" : "s"}.
                Review before signing.
              </p>
            </div>
            <Link
              to="/documents"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
              data-testid="riskier-view-all"
            >
              View all docs <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {riskier.slice(0, 4).map((r) => {
              const lvl = LEVEL[r.level] || LEVEL.medium;
              return (
                <li
                  key={r.group_id}
                  className="rounded-xl bg-white border border-rose-200 p-4 hover:shadow-sm transition cursor-pointer"
                  onClick={() => navigate(`/documents`)}
                  data-testid={`riskier-row-${r.group_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{r.doc_name}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                        v{r.previous_version} → v{r.latest_version}
                      </div>
                    </div>
                    <span className={"shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold " + lvl.bg + " " + lvl.color}>
                      {lvl.label}
                    </span>
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-2">
                    <div className="text-sm text-slate-600">
                      Score{" "}
                      <span className="font-semibold text-slate-900 tabular-nums">{r.previous_score}</span>
                      {" → "}
                      <span className="font-semibold text-rose-700 tabular-nums">{r.latest_score}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 tabular-nums">
                      <TrendingDown className="h-3.5 w-3.5" /> {r.delta}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Issues + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="open-issues-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">Pending issues</h2>
            <Link to="/scans" className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-slate-100">
            {(data?.recent_issues || []).length === 0 && (
              <li className="py-10 text-center text-sm text-slate-500">
                No open issues. Upload a document and run a scan to get started.
              </li>
            )}
            {(data?.recent_issues || []).map((i) => (
              <li key={i.id} className="py-3 flex items-start gap-3" data-testid={`issue-row-${i.id}`}>
                <span className={"shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold " + SEV[i.severity]}>
                  {i.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{i.title}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">{i.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="activity-card">
          <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">Recent activity</h2>
          <ul className="mt-4 space-y-3">
            {(data?.recent_activity || []).length === 0 && (
              <li className="text-sm text-slate-500">No activity yet.</li>
            )}
            {(data?.recent_activity || []).map((a, i) => (
              <li key={i} className="text-sm flex gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                <span className="flex-1">
                  <span className="font-medium text-slate-900">{a.actor_name}</span>{" "}
                  <span className="text-slate-600">{a.action}</span>{" "}
                  <span className="text-slate-500">{a.target}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-semibold tabular-nums text-slate-900">
        {value ?? "—"}
      </div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </div>
  );
}
